import React, { useContext, useEffect, useState } from 'react';

import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InstantSearch, Configure, Hits } from 'react-instantsearch-dom';

import { ExpertCard } from '../../../../components/ExpertCard';
import { Loader } from '../../../../components/Loader';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import { BREAKPOINTS_VALUES } from '../../../../theme';
import { Tenant } from '../../../../types';
import { translate } from '../../../../utils/helpers';
import { useClearSearchState } from '../../../../utils/hooks';
import Breadcrumbs from '../../components/Breadcrumbs';
import EmptyState from '../../components/EmptyState';
import { ExpertsFilterPanel } from '../../components/ExpertsFilterPanel';
import { ExpertsSearch } from '../../components/ExpertsSearch';
import { ExpertsSeoBlock } from '../../components/ExpertsSeoBlock';
import { FilterWrapper } from '../../components/FilterWrapper';
import { FiltersOverlay } from '../../components/FiltersOverlay';
import { Header } from '../../components/Header';
import { useCommonHeaderStyles } from '../../components/Header/style';
import { SearchResults } from '../../components/SearchResults';
import { DEFAULT_URL } from '../../const';
import useStyles from './styles';

interface ExpertsPageProps {
  tenant: Tenant;
  searchClient: any;
  loadingKey: boolean;
  logoUrl?: string;
}

const navigationArr = [
  {
    title: translate('page.discover.discover'),
    url: DEFAULT_URL,
  },
  {
    title: translate('page.discover.experts'),
    url: '/experts',
  },
];

const srcset = [
  { width: 220, height: 220, crop: 'maintain_ratio', focus: 'auto' },
  { width: 275, height: 220, crop: 'maintain_ratio', focus: 'auto' },
  { width: 270, height: 250, crop: 'maintain_ratio', focus: 'auto' },
  { width: 240, height: 280, crop: 'maintain_ratio', focus: 'auto' },
];
const sizes = `(max-width:${BREAKPOINTS_VALUES['sm']}px) 220px, (max-width:600px) 275px, (max-width:${BREAKPOINTS_VALUES['xl']}px) 270px, 240px`;
const transformation = [
  {
    width: 240,
    height: 280,
    focus: 'auto',
  },
];

const bgImage = '/page-images/expertsBg.png';
const bgSuImage = '/page-images/expertsSuBg.png';

const ExpertsPage: React.FC<ExpertsPageProps> = ({ searchClient, tenant, loadingKey, logoUrl }) => {
  const { t } = useTranslation();
  const classes = { ...useStyles(), ...useCommonHeaderStyles() };
  const algoliaIndexName = process.env.REACT_APP_ALGOLIA_EXPERTS_INDEX_NAME;

  const { searchState, setSearchState } = useContext(DiscoverContext);

  const [canClearFilters, setCanClearFilters] = useState(false);
  const [itemsCount, setItemsCount] = useState<null | number>(null);

  const headerImage = tenant?.name === 'sustainabilityunlocked' ? bgSuImage : bgImage;

  //clear search state on page change
  useClearSearchState();

  const handleResetFilters = () => {
    setSearchState((prevState) => ({
      query: prevState.query,
    }));
  };

  const resultsFunc = (count) => {
    return count > 1 || !count ? t('page.discover.experts') : t('page.discover.result');
  };

  const handleClear = () => {
    setSearchState((prevState) => ({
      ...prevState,
      query: '',
    }));
  };

  useEffect(() => {
    setCanClearFilters(!!searchState?.hierarchicalMenu);
  }, [searchState]);

  if (loadingKey) {
    return (
      <>
        <ExpertsSeoBlock tenantTitle={tenant?.title} seoImage={headerImage} />
        <Loader />
      </>
    );
  }

  return (
    <>
      <ExpertsSeoBlock tenantTitle={tenant?.title} seoImage={headerImage} />
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        searchClient={searchClient}
        indexName={algoliaIndexName}
      >
        <Header
          imageUrl={headerImage}
          wrapperClass={classes.headerWrapper}
          contentWrapperClass={classes.headerContent}
          imageWrapperClass={classes.headerImgWrapper}
          tenantName={tenant?.name}
        >
          <Container>
            <Breadcrumbs navigation={navigationArr} />
            <Box>
              <Typography variant="h2" className={classes.searchTitle}>
                {t('page.discover.experts')}
              </Typography>
              <Typography className={classes.searchDescription}>
                {t('page.discover.expertsDescription')}
              </Typography>
            </Box>
          </Container>
        </Header>
        <Container className={classes.bodyWrapper}>
          <Box>
            <FilterWrapper
              wrapperClass={classes.expertsSearchWrapper}
              title={t('page.discover.experts')}
              withoutToggle
            >
              <ExpertsSearch handleClear={handleClear} />
            </FilterWrapper>
            <FiltersOverlay
              logoUrl={logoUrl}
              handleResetFilters={handleResetFilters}
              canClearFilters={canClearFilters}
            >
              <ExpertsFilterPanel tenantName={tenant?.name} />
            </FiltersOverlay>
          </Box>
          <Box className={classes.results}>
            <Configure hitsPerPage={1000} />
            <SearchResults resultsFunc={resultsFunc} setItemsCount={setItemsCount} />
            <Box className={classes.expertCardWrapper}>
              <Hits
                hitComponent={({ hit }) => (
                  <ExpertCard
                    key={hit?.id}
                    title={hit?.name}
                    jobTitle={hit?.job_title}
                    imageUrl={hit?.avatar}
                    slug={hit?.slug}
                    wrapperClass={classes.expertCard}
                    srcset={srcset}
                    sizes={sizes}
                    transformation={transformation}
                    withLink
                  />
                )}
              />
            </Box>
            {itemsCount === 0 && <EmptyState withSubTitle />}
          </Box>
        </Container>
      </InstantSearch>
    </>
  );
};

export default ExpertsPage;
